import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import RichEditor from "../../components/admin/RichEditor";
import {
  Mail, Save, Loader2, Send, Server, Paperclip, Info, Sparkles, ChevronDown, ChevronRight, RotateCcw, Eye, X,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_ORDER = ["paid", "preparing", "ready", "shipped", "collected", "completed", "refunded", "cancelled"];
const STATUS_COLORS = {
  paid: "bg-emerald-100 text-emerald-700",
  preparing: "bg-sky-100 text-sky-700",
  ready: "bg-indigo-100 text-indigo-700",
  shipped: "bg-blue-100 text-blue-700",
  collected: "bg-violet-100 text-violet-700",
  completed: "bg-slate-200 text-slate-700",
  refunded: "bg-amber-100 text-amber-700",
  cancelled: "bg-rose-100 text-rose-700",
};

export default function EmailsAdmin() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState({
    smtp_host: "",
    smtp_port: 465,
    smtp_user: "",
    smtp_password: "",
    smtp_use_ssl: true,
    smtp_from_name: "",
    smtp_from_email: "",
    notification_email: "",
    invoice_email_enabled: true,
    order_email_templates: {},
  });
  const [defaults, setDefaults] = useState({ defaults: {}, status_labels: {}, placeholders: [] });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testStatus, setTestStatus] = useState("paid");
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState({ paid: true });
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewStatus, setPreviewStatus] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/order-email-defaults").then(r => setDefaults(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!settings) return;
    setForm({
      smtp_host: settings.smtp_host || "",
      smtp_port: settings.smtp_port ?? 465,
      smtp_user: settings.smtp_user || "",
      smtp_password: settings.smtp_password || "",
      smtp_use_ssl: settings.smtp_use_ssl !== false,
      smtp_from_name: settings.smtp_from_name || "",
      smtp_from_email: settings.smtp_from_email || "",
      notification_email: settings.notification_email || "",
      invoice_email_enabled: settings.invoice_email_enabled !== false,
      order_email_templates: settings.order_email_templates || {},
    });
    setDirty(false);
  }, [settings]);

  useEffect(() => {
    if (!testEmail && settings?.notification_email) setTestEmail(settings.notification_email);
  }, [settings, testEmail]);

  const patch = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const patchTemplate = (status, field, value) => {
    const next = { ...(form.order_email_templates || {}) };
    next[status] = { ...(next[status] || {}), [field]: value };
    setForm(f => ({ ...f, order_email_templates: next }));
    setDirty(true);
  };

  const resetTemplate = (status) => {
    const next = { ...(form.order_email_templates || {}) };
    delete next[status];
    setForm(f => ({ ...f, order_email_templates: next }));
    setDirty(true);
    toast.success("Template réinitialisé");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      await refresh();
      setDirty(false);
      toast.success("Configuration email enregistrée");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error("Adresse destinataire requise"); return; }
    setSending(true);
    try {
      const tpl = form.order_email_templates?.[testStatus] || {};
      await api.post("/admin/order-emails/test", {
        to: testEmail,
        status: testStatus,
        subject: tpl.subject || undefined,
        body: tpl.body || undefined,
      });
      toast.success(`Email de test envoyé à ${testEmail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi échoué");
    } finally { setSending(false); }
  };

  const sendSmtpTest = async () => {
    if (!testEmail) { toast.error("Adresse destinataire requise"); return; }
    setSending(true);
    try {
      const r = await api.post("/settings/test-smtp", { to: testEmail });
      if (r?.data?.sent === false) {
        const reason = r.data.reason === "smtp_not_configured"
          ? "SMTP non configuré — remplissez les champs ci-dessus et enregistrez avant de tester"
          : (r.data.reason || "Envoi SMTP échoué");
        toast.error(reason);
      } else {
        toast.success(`Test SMTP envoyé à ${testEmail}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test SMTP échoué");
    } finally { setSending(false); }
  };

  const getTemplateValue = (status, field) => {
    const custom = form.order_email_templates?.[status]?.[field];
    if (custom !== undefined && custom !== null) return custom;
    return defaults.defaults?.[status]?.[field] || "";
  };

  const openPreview = async (status) => {
    setPreviewStatus(status);
    setPreviewHtml(null);
    setPreviewLoading(true);
    try {
      const tpl = form.order_email_templates?.[status] || {};
      const r = await api.post("/admin/order-emails/preview", {
        status,
        subject: tpl.subject || undefined,
        body: tpl.body || undefined,
      });
      setPreviewHtml(r.data);
    } catch (e) {
      toast.error("Erreur preview : " + (e?.response?.data?.detail || e.message));
      setPreviewStatus(null);
    } finally { setPreviewLoading(false); }
  };

  const closePreview = () => { setPreviewStatus(null); setPreviewHtml(null); };

  const isCustomized = (status) => !!(form.order_email_templates?.[status] && (form.order_email_templates[status].subject || form.order_email_templates[status].body));

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="emails-admin">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <Mail className="w-3.5 h-3.5" /> Emails de notifications
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Emails &amp; SMTP</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Configurez le serveur SMTP d'envoi, personnalisez les messages envoyés à vos clients à chaque étape d'une commande, et testez la configuration avant mise en production.
          </p>
        </div>
        <Button onClick={save} disabled={!dirty || saving} className="bg-[color:var(--brand-navy)]" data-testid="emails-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          {dirty ? "Enregistrer" : "Enregistré"}
        </Button>
      </header>

      {/* SMTP */}
      <Card className="p-5 space-y-4" data-testid="emails-smtp-section">
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4 text-[color:var(--brand-sky)]" />
          <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Serveur SMTP (expéditeur)</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs font-bold">Serveur SMTP</Label>
            <Input value={form.smtp_host} onChange={e => patch("smtp_host", e.target.value)} placeholder="mail.votre-domaine.fr" className="mt-1.5" data-testid="smtp-host" />
          </div>
          <div>
            <Label className="text-xs font-bold">Port</Label>
            <Input type="number" value={form.smtp_port} onChange={e => patch("smtp_port", +e.target.value)} className="mt-1.5" data-testid="smtp-port" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-bold">Identifiant</Label>
            <Input value={form.smtp_user} onChange={e => patch("smtp_user", e.target.value)} placeholder="noreply@votre-domaine.fr" className="mt-1.5" data-testid="smtp-user" />
          </div>
          <div>
            <Label className="text-xs font-bold">Mot de passe</Label>
            <Input type="password" value={form.smtp_password} onChange={e => patch("smtp_password", e.target.value)} placeholder="••••••••" className="mt-1.5" data-testid="smtp-password" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-bold">Nom expéditeur</Label>
            <Input value={form.smtp_from_name} onChange={e => patch("smtp_from_name", e.target.value)} placeholder="[Votre Entreprise]" className="mt-1.5" data-testid="smtp-from-name" />
          </div>
          <div>
            <Label className="text-xs font-bold">Email expéditeur (From)</Label>
            <Input value={form.smtp_from_email} onChange={e => patch("smtp_from_email", e.target.value)} placeholder="noreply@votre-domaine.fr" className="mt-1.5" data-testid="smtp-from-email" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-bold">Email interne pour notifications (leads, alertes)</Label>
            <Input value={form.notification_email} onChange={e => patch("notification_email", e.target.value)} placeholder="contact@votre-domaine.fr (, autre@domaine.fr)" className="mt-1.5" data-testid="notification-email" />
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Séparez plusieurs destinataires par une virgule.</div>
          </div>
          <label className="flex items-start gap-3 p-3 rounded-xl border bg-[hsl(var(--muted))]/30 cursor-pointer h-fit mt-auto" data-testid="smtp-ssl-wrapper">
            <Switch checked={form.smtp_use_ssl} onCheckedChange={v => patch("smtp_use_ssl", v)} />
            <div>
              <div className="text-sm font-bold text-[color:var(--brand-navy)]">Connexion SSL/TLS</div>
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Activé par défaut (port 465). Désactivez si le serveur utilise STARTTLS (port 587).</div>
            </div>
          </label>
        </div>
      </Card>

      {/* Test SMTP */}
      <Card className="p-5 space-y-3 border-sky-200 bg-sky-50/30" data-testid="emails-test-section">
        <div className="flex items-center gap-2">
          <Send className="w-4 h-4 text-[color:var(--brand-sky)]" />
          <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Tester la configuration</h3>
        </div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] bg-white rounded-lg p-3 border flex items-start gap-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-sky-600" />
          <div>
            <strong>Avant de publier votre site</strong>, envoyez un email de test pour valider la configuration SMTP. Vous pouvez aussi tester le rendu d'un email de notification de commande spécifique.
          </div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs font-bold">Adresse email destinataire</Label>
            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="votre-email@exemple.fr" className="mt-1.5" data-testid="test-email-to" />
          </div>
          <div>
            <Label className="text-xs font-bold">Statut à tester</Label>
            <select
              value={testStatus}
              onChange={e => setTestStatus(e.target.value)}
              className="mt-1.5 h-10 rounded-md border px-3 text-sm bg-white"
              data-testid="test-email-status"
            >
              {STATUS_ORDER.map(s => <option key={s} value={s}>{defaults.status_labels?.[s] || s}</option>)}
            </select>
          </div>
          <Button onClick={sendTest} disabled={sending} className="bg-[color:var(--brand-sky)]" data-testid="send-test-order-email">
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
            Envoyer le test de notification
          </Button>
          <Button variant="outline" onClick={sendSmtpTest} disabled={sending} data-testid="send-test-smtp">
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Server className="w-4 h-4 mr-1.5" />}
            Tester le SMTP (email simple)
          </Button>
        </div>
        <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
          Le test "notification" utilise le template {defaults.status_labels?.[testStatus]?.toLowerCase() || testStatus} — si vous avez modifié le sujet ou le corps sans enregistrer, la preview reflète vos modifications en cours.
        </div>
      </Card>

      {/* Toggle facture en pièce jointe */}
      <Card className="p-5 space-y-3" data-testid="emails-invoice-attach-section">
        <div className="flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-[color:var(--brand-sky)]" />
          <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Facture PDF en pièce jointe</h3>
        </div>
        <label className="flex items-start gap-3 p-3 rounded-xl border bg-[hsl(var(--muted))]/30 cursor-pointer" data-testid="invoice-email-enabled-wrapper">
          <Switch checked={form.invoice_email_enabled} onCheckedChange={v => patch("invoice_email_enabled", v)} />
          <div>
            <div className="text-sm font-bold text-[color:var(--brand-navy)]">Joindre automatiquement la facture PDF à l'email « Paiement confirmé »</div>
            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Activé : le client reçoit sa facture dès la validation du paiement. Désactivé : la facture reste disponible depuis son espace client.</div>
          </div>
        </label>
      </Card>

      {/* Templates */}
      <Card className="p-5 space-y-4" data-testid="emails-templates-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[color:var(--brand-sky)]" />
            <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Messages de notification</h3>
          </div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
            Placeholders :
            {defaults.placeholders?.map(p => (
              <code key={p} className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono text-[10px]">{p}</code>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          {STATUS_ORDER.map(status => {
            const isOpen = !!expanded[status];
            const customized = isCustomized(status);
            return (
              <div key={status} className="rounded-xl border bg-white overflow-hidden" data-testid={`template-${status}`}>
                <button
                  onClick={() => setExpanded(e => ({ ...e, [status]: !e[status] }))}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-[hsl(var(--muted))]/20 transition"
                  data-testid={`template-toggle-${status}`}
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <Badge className={`${STATUS_COLORS[status]} border-0 text-[10px] font-bold`}>{defaults.status_labels?.[status] || status}</Badge>
                    <span className="text-sm font-semibold text-[color:var(--brand-navy)]">
                      {getTemplateValue(status, "subject")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {customized && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Personnalisé</span>}
                    {status === "paid" && form.invoice_email_enabled && <span className="text-[10px] uppercase tracking-wider font-bold text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded inline-flex items-center gap-1"><Paperclip className="w-2.5 h-2.5" /> PDF</span>}
                  </div>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 space-y-3 border-t pt-3 bg-slate-50/40">
                    <div>
                      <Label className="text-xs font-bold">Objet</Label>
                      <Input
                        value={getTemplateValue(status, "subject")}
                        onChange={e => patchTemplate(status, "subject", e.target.value)}
                        className="mt-1.5 text-sm"
                        data-testid={`template-subject-${status}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-bold">Corps de l'email <span className="text-[10px] text-[hsl(var(--muted-foreground))] font-normal ml-1">(éditeur visuel — gras, liens, listes, images...)</span></Label>
                      <div className="mt-1.5" data-testid={`template-body-${status}`}>
                        <RichEditor
                          value={getTemplateValue(status, "body")}
                          onChange={(html) => patchTemplate(status, "body", html)}
                          placeholder="Rédigez votre message…"
                          minHeight={220}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {customized && (
                        <Button variant="outline" size="sm" onClick={() => resetTemplate(status)} className="text-xs" data-testid={`template-reset-${status}`}>
                          <RotateCcw className="w-3 h-3 mr-1" /> Rétablir le texte par défaut
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPreview(status)}
                        className="text-xs"
                        data-testid={`template-preview-${status}`}
                      >
                        <Eye className="w-3 h-3 mr-1" /> Aperçu de l'email
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setTestStatus(status); document.querySelector('[data-testid="test-email-to"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                        className="text-xs"
                        data-testid={`template-test-${status}`}
                      >
                        <Send className="w-3 h-3 mr-1" /> Tester cet email
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {dirty && (
        <div className="sticky bottom-4 flex justify-center z-30">
          <Card className="p-3 bg-[color:var(--brand-navy)] text-white border-0 shadow-2xl flex items-center gap-3">
            <span className="text-xs">Modifications non enregistrées</span>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[color:var(--brand-sky)]">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Enregistrer
            </Button>
          </Card>
        </div>
      )}

      {/* Preview Modal */}
      {previewStatus && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closePreview} data-testid="email-preview-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-[color:var(--brand-sky)]" />
                <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)]">Aperçu de l'email</h3>
                <Badge className={`${STATUS_COLORS[previewStatus]} border-0 text-[10px] font-bold`}>
                  {defaults.status_labels?.[previewStatus] || previewStatus}
                </Badge>
              </div>
              <button onClick={closePreview} className="p-1.5 rounded-md hover:bg-slate-200 transition" data-testid="email-preview-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {previewLoading || !previewHtml ? (
              <div className="flex-1 flex items-center justify-center py-16 text-sm text-[hsl(var(--muted-foreground))]">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Chargement de l'aperçu…
              </div>
            ) : (
              <>
                <div className="px-5 py-3 border-b bg-slate-50/60 text-xs">
                  <div className="flex items-baseline gap-2"><span className="font-bold text-slate-500 uppercase tracking-wider">Sujet :</span><span className="font-semibold text-[color:var(--brand-navy)]">{previewHtml.subject}</span></div>
                  <div className="flex items-baseline gap-2 mt-0.5"><span className="font-bold text-slate-500 uppercase tracking-wider">À :</span><span>client@exemple.fr</span></div>
                </div>
                <iframe
                  title="Aperçu email"
                  srcDoc={previewHtml.html}
                  className="flex-1 w-full bg-slate-100 min-h-[500px]"
                  sandbox=""
                  data-testid="email-preview-iframe"
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
